import { endpoints } from '@api'
import { Box, useToast } from '@chakra-ui/react'
import { datadogLogs } from '@datadog/browser-logs'
import type { TelehealthAudioSource } from '@handlers/sessions/components/telehealth-toggle'
import { useExperienceManager } from '@hooks'
import { type Actions, useStoreActions, useStoreState } from 'easy-peasy'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useMutation, useQueryClient } from 'react-query'
import type { StoreModel } from 'src/store/types'
import * as uuid from 'uuid'
import { AudioCaptureContext } from './audio-capture-context'
import type { WarningType } from './types'
import {
  isChromiumBrowser,
  isWholeScreen,
  isWholeWindow,
  isWindows,
  playAlertSound
} from './utils'

interface ManualAudioCaptureProviderProps {
  sessionId?: string
  refetchRecap?: () => void
  isDictation?: boolean
  children: React.ReactNode
}

interface UploadPayload {
  timestamp: number
  chunks: BlobPart[]
}

interface BatteryManager {
  level: number
  charging: boolean
  chargingTime: number
  dischargingTime: number
}

const LOCAL_STORAGE_KEY = 'sessionRecordings'

const ManualAudioCaptureProviderV2: React.FC<
  ManualAudioCaptureProviderProps
> = ({ sessionId, refetchRecap, isDictation = false, children }) => {
  const [isRecording, setIsRecording] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
  const [selectedDevice, setSelectedDevice] = useState<MediaDeviceInfo | null>(
    null
  )
  const [selectedDeviceLabel, setSelectedDeviceLabel] = useState('')
  const [permissionStatus, setPermissionStatus] = useState<string | null>(null)
  const [hasMicAccess, setHasMicAccess] = useState(true)
  const [localRecordingId] = useState<string>(uuid.v4())
  const [isSettingsOpen, setIsSettingsOpen] = useState<boolean>(false)
  const [isStopRecordingAfterEnabled, setIsStopRecordingAfterEnabled] =
    useState<boolean>(false)
  const [stopRecordingAfter, setStopRecordingAfter] = useState<string>('')

  const [uploadQueue, setUploadQueue] = useState<UploadPayload[]>([])
  const [isLastChunkSet, setIsLastChunkSet] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [isUploadComplete, setIsUploadComplete] = useState(false)

  const [isTesting, setIsTesting] = useState<boolean>(false)
  const [disableEchoCancelation, setDisableEchoCancelation] = useState(false)
  const [hasDetectedAudio, setHasDetectedAudio] = useState<boolean>(false)
  const [hasDetectedTabAudio, setHasDetectedTabAudio] = useState<boolean>(false)
  const [audioLastDetectedAt, setAudioLastDetectedAt] = useState<number | null>(
    null
  )
  const [tabAudioLastDetectedAt, setTabAudioLastDetectedAt] = useState<
    number | null
  >(null)

  // Active warning state is stored in a ref so that async functions like 'tick' can access the current state
  const activeWarningRef = useRef<WarningType | null>(null)
  const [, forceUpdate] = useState({})
  const setActiveWarning: React.Dispatch<
    React.SetStateAction<WarningType | null>
  > = warning => {
    const newValue =
      typeof warning === 'function'
        ? warning(activeWarningRef.current)
        : warning
    // Only force re-render if the value actually changed
    if (activeWarningRef.current !== newValue) {
      activeWarningRef.current = newValue
      // We force a re-render to update the active warning state in the UX
      forceUpdate({})
    }
  }

  const showMicWarning = activeWarningRef.current === 'mic'
  const showTabMicWarning = activeWarningRef.current === 'tab'
  const showScreenShareWarning = activeWarningRef.current === 'screenshare'
  const showAudioStreamWarning = activeWarningRef.current === 'audio-stream'

  const [isTelehealth, setIsTelehealth] = useState<boolean | null>(null)
  const [isUsingHeadphones, setIsUsingHeadphones] = useState(false)
  const [telehealthAudioSource, setTelehealthAudioSource] =
    useState<TelehealthAudioSource | null>(null)
  const [qualityToggle, setQualityToggle] = useState<boolean | null>(null)
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [telehealthSourceError, setTelehealthSourceError] = useState<
    string | null
  >(null)
  const [
    hasResolvedInputDevicePermissions,
    setHasResolvedInputDevicePermissions
  ] = useState<boolean>(false)

  const [contentShareDisplayType, setContentShareDisplayType] =
    useState<string>('')
  const [uploadCount, setUploadCount] = useState(0)
  const [uploadUrl, setUploadUrl] = useState<string | null>(null)
  const [uploadToken, setUploadToken] = useState<string | null>(null)
  const [batteryInfo, setBatteryInfo] = useState<BatteryManager | null>(null)
  const [wakeLockEnabled, setWakeLockEnabled] = useState(false)

  const audioStreamRef = useRef<MediaStream | null>(null)
  const selectedDeviceLabelRef = useRef<string>(selectedDeviceLabel)
  const streamsRef = useRef<MediaStream[]>([])
  const tabStreamRef = useRef<MediaStream | null | undefined>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const isRecordingRef = useRef<boolean>(isRecording)
  const isLastChunkSetRef = useRef<boolean>(false)
  const isUploadCompleteRef = useRef<boolean>(isUploadComplete)
  const isTestingRef = useRef<boolean>(isTesting)
  const audioContextRef = useRef<AudioContext | null>(null)
  const uploadQueueRef = useRef<UploadPayload[]>(uploadQueue)
  const mediaRecordersRef = useRef<MediaRecorder[]>([])
  const timerRef = useRef<number | null>(null)
  const selectedDeviceRef = useRef<MediaDeviceInfo | null>(selectedDevice)
  const maxVolumeRef = useRef<number>(0)
  const maxTabVolumeRef = useRef<number>(0)
  const lastSuccessfulUploadAtRef = useRef<number | null>(null)
  const retryCountRef = useRef<number>(0)
  const uploadTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const tickIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const volumeActions = useStoreActions(
    (actions: Actions<StoreModel>) => actions.volume
  )

  const queryClient = useQueryClient()

  const toast = useToast()

  const { user } = useStoreState((state: StoreModel) => state.auth)

  const {
    documentationAutomationFreeTierSessionLimitReached,
    isAdmin,
    isPresignedUrlV2Enabled,
    isAudioScreenshareWarningEnabled,
    isClientChanneledAudioEnabled: isClientChanneledAudioEnabledFlag,
    isValidateAudioStreamEnabled,
    isTelehealthSourceRequiredEnabled: _isTelehealthSourceRequiredEnabled,
    isExtension
  } = useExperienceManager()

  const isTelehealthSourceRequiredEnabled =
    !!isTelehealth &&
    _isTelehealthSourceRequiredEnabled &&
    isChromiumBrowser() &&
    !isDictation

  const isClientChanneledAudioEnabled = () => {
    return !!(tabStreamRef.current && isClientChanneledAudioEnabledFlag)
  }

  const selectedAudioInputLabel = devices.find(
    i => i.deviceId === selectedDevice?.deviceId
  )?.label

  const logDataRef = useRef({})

  const { mutateAsync: updateSession, isLoading: isUpdateSessionLoading } =
    useMutation(endpoints.startAudioCapture.request, {
      onSuccess: (data: any) => {
        if (data.audioUpload?.url) {
          setUploadUrl(data.audioUpload.url)
          setUploadToken(data.audioUpload.token)
        }
        refetchRecap && refetchRecap()
      }
    })

  const { mutateAsync: getPresignedUrl } = useMutation(
    endpoints.getPresignedUrl.request
  )

  const { mutateAsync: completeScribeOnboarding } = useMutation(
    endpoints.postUserAccount.request,
    {
      onSuccess() {
        queryClient.invalidateQueries(endpoints.getUserAccount.getCacheId())
      }
    }
  )

  const handleMarkOnboardingComplete = async () => {
    if (!user?.has_onboarded_to_scribe) {
      await completeScribeOnboarding({
        data: { has_onboarded_to_scribe: true }
      })
    }
  }

  logDataRef.current = {
    isV2: true,
    sessionId,
    isRecording,
    isTesting,
    hasMicAccess,
    uploadQueue: uploadQueue.length,
    hasDetectedAudio,
    hasDetectedTabAudio,
    audioLastDetectedAt,
    isTelehealth,
    isUsingHeadphones,
    telehealthAudioSource,
    qualityToggle,
    isMuted,
    isUploading,
    isUploadComplete,
    isLastChunkSet: isLastChunkSetRef.current,
    showMicWarning,
    showTabMicWarning,
    showScreenShareWarning,
    showAudioStreamWarning,
    activeWarning: activeWarningRef.current,
    disableEchoCancelation,
    devices,
    selectedAudioInput: selectedDevice?.deviceId,
    selectedAudioInputLabel,
    documentationAutomationFreeTierSessionLimitReached,
    isAdmin,
    isOnline,
    stopRecordingAfter,
    isStopRecordingAfterEnabled,
    rollingUploadCount: uploadCount,
    uploadUrl,
    maxVolume: maxVolumeRef.current,
    maxTabVolume: maxTabVolumeRef.current,
    contentShareDisplayType,
    lastSuccessfulUploadAt: lastSuccessfulUploadAtRef.current,
    retryCount: retryCountRef.current,
    batteryLevel: batteryInfo?.level,
    batteryCharging: batteryInfo?.charging,
    batteryChargingTime: batteryInfo?.chargingTime,
    batteryDischargingTime: batteryInfo?.dischargingTime,
    wakeLockEnabled,
    isClientChanneledAudioEnabled: isClientChanneledAudioEnabled()
  }

  useEffect(() => {
    let wakeLock: any = null

    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator && document.visibilityState === 'visible') {
          // @ts-ignore
          wakeLock = await navigator.wakeLock.request('screen')
          setWakeLockEnabled(true)

          wakeLock.addEventListener('release', () => {
            setWakeLockEnabled(false)
          })
        }
      } catch (err) {
        setWakeLockEnabled(false)
        datadogLogs.logger.warn('[Session] Unable to request wake lock', {
          err: JSON.stringify(err),
          ...logDataRef.current
        })
      }
    }

    requestWakeLock()

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        requestWakeLock()
      } else if (wakeLock) {
        wakeLock.release().catch(() => {})
        setWakeLockEnabled(false)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (wakeLock) {
        wakeLock.release().catch(() => {
          datadogLogs.logger.warn('[Session] Unable to release wake lock', {
            ...logDataRef.current
          })
        })
      }
    }
  }, [])

  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined
    let battery: BatteryManager | null = null

    async function pollBatteryLevels() {
      if ('getBattery' in navigator) {
        try {
          battery = await (navigator as any).getBattery()
          setBatteryInfo(battery)

          intervalId = setInterval(() => {
            setBatteryInfo(battery)
          }, 30000)
        } catch (err) {
          datadogLogs.logger.warn('[Audio Capture] Battery API error', {
            err: JSON.stringify(err),
            ...logDataRef.current
          })
        }
      } else {
        datadogLogs.logger.warn(
          '[Audio Capture] Battery Status API not supported on this browser.',
          {
            ...logDataRef.current
          }
        )
      }
    }

    pollBatteryLevels()

    return () => {
      if (intervalId) clearInterval(intervalId)
    }
  }, [])

  useEffect(() => {
    if (devices.length && !selectedDevice) {
      const lastDeviceId = localStorage.getItem('microphoneDeviceId')
      const device =
        devices.find(d => d.deviceId === lastDeviceId) || devices[0]

      setSelectedDevice(device)
      setSelectedDeviceLabel(device.label)
      datadogLogs.logger.info('[Audio Capture] Device Set', {
        ...logDataRef.current,
        deviceId: device.deviceId
      })
    }
  }, [devices, selectedDevice])

  useEffect(() => {
    if (
      isRecordingRef.current &&
      !isTestingRef.current &&
      !isSettingsOpen &&
      selectedDeviceLabel &&
      selectedDeviceLabel !== selectedDeviceLabelRef.current &&
      selectedDeviceLabel !== 'Default'
    ) {
      datadogLogs.logger.info('[Audio Capture] Showing device changed', {
        ...logDataRef.current,
        newDevice: selectedDeviceLabel
      })
      toast({
        position: 'top',
        isClosable: true,
        duration: 4000,
        containerStyle: {
          width: '800px',
          maxWidth: '100%'
        },
        render: () => (
          <Box
            p={2}
            bg="black"
            border="1px solid white"
            color="white"
            borderRadius="8px"
            textAlign="center"
          >
            Your default microphone has changed to {selectedDeviceLabel} and
            will now be used.
          </Box>
        )
      })
    }
  }, [selectedDeviceLabel])

  useEffect(() => {
    const handleDeviceChange = async () => {
      datadogLogs.logger.info(
        '[Audio Capture] Device Changed Event',
        logDataRef.current
      )
      const microphoneDevices = await getInputDevices()
      const isSelectedDeviceRemoved =
        microphoneDevices.filter(
          d =>
            d.deviceId === selectedDeviceRef.current?.deviceId &&
            d.groupId === selectedDeviceRef.current?.groupId
        ).length === 0

      if (isSelectedDeviceRemoved) {
        setSelectedDevice(null)
      }
    }

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange)
    return () => {
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        handleDeviceChange
      )
    }
  }, [])

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!document.hidden && isRecordingRef.current && isMobile) {
        datadogLogs.logger.info(
          '[Audio Capture] Mobile tab visible',
          logDataRef.current
        )
        await refreshMediaRecorder()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  useEffect(() => {
    const handleOnlineStatusChange = () => {
      setIsOnline(navigator.onLine)
    }

    window.addEventListener('online', handleOnlineStatusChange)
    window.addEventListener('offline', handleOnlineStatusChange)

    return () => {
      window.removeEventListener('online', handleOnlineStatusChange)
      window.removeEventListener('offline', handleOnlineStatusChange)
    }
  }, [])

  useEffect(() => {
    const handleStorageChange = async (event: any) => {
      if (event.key === LOCAL_STORAGE_KEY) {
        try {
          const values = JSON.parse(event.newValue) || {}
          const value = values[sessionId!]
          if (value && value !== localRecordingId) {
            datadogLogs.logger.info(
              '[Audio Capture] Auto-stopping duplicate tab recording',
              logDataRef.current
            )
            await stopRecording()
          }
        } catch (err) {
          datadogLogs.logger.warn(
            '[Audio Capture] Unable to parse localStorage',
            {
              ...logDataRef.current
            },
            err as Error
          )
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  // keep refs in sync w/ state
  useEffect(() => {
    selectedDeviceLabelRef.current = selectedDeviceLabel
  }, [selectedDevice])

  useEffect(() => {
    if (telehealthAudioSource || !isTelehealth) {
      setTelehealthSourceError(null)
    }
  }, [telehealthAudioSource, isTelehealth])

  useEffect(() => {
    selectedDeviceRef.current = selectedDevice
  }, [selectedDevice])

  useEffect(() => {
    isTestingRef.current = isTesting
  }, [isTesting])

  useEffect(() => {
    uploadQueueRef.current = uploadQueue
  }, [uploadQueue])

  useEffect(() => {
    datadogLogs.logger.info('[Audio Capture] Online status changed', {
      ...logDataRef.current,
      isOnline
    })
  }, [isOnline])

  useEffect(() => {
    // timer to track how many chunks of audio are uploaded in a 60 second window
    if (!timerRef.current) {
      timerRef.current = window.setInterval(() => {
        setUploadCount(0)
      }, 60000)
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [])

  // clean up audio streams on unmount
  useEffect(() => {
    return () => {
      datadogLogs.logger.info('[Audio Capture] Unmounted', {
        ...logDataRef.current
      })
      stopAudioStream()
      stopRecording()
      if (uploadTimeoutRef.current) {
        clearTimeout(uploadTimeoutRef.current)
        uploadTimeoutRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    async function resolveInputDevicePermissionStatus() {
      try {
        const permissionStatus = await navigator.permissions.query({
          name: 'microphone' as PermissionName
        })

        if (permissionStatus.state === 'denied') {
          setPermissionStatus('denied')
          setHasMicAccess(false)
        } else if (permissionStatus.state === 'granted') {
          setPermissionStatus('granted')
          await getInputDevices()
        } else {
          setPermissionStatus('unknown')
        }

        setHasResolvedInputDevicePermissions(true)
      } catch (error) {
        console.error('Error querying microphone permission:', error)

        // Fallback for browsers that don't support the Permissions API
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true
          })
          if (stream) {
            setPermissionStatus('granted')
            setHasMicAccess(true)
            await getInputDevices()
          } else {
            setPermissionStatus('denied')
            setHasMicAccess(false)
          }

          setHasResolvedInputDevicePermissions(true)
        } catch (e) {
          console.error('Error granting microphone permission with fallback', e)
        }
      }
    }
    resolveInputDevicePermissionStatus()
  }, [])

  useEffect(() => {
    if (uploadQueue.length > 0 && !isUploading) {
      uploadChunk(uploadQueue[0])
    }
  }, [uploadQueue, isUploading])

  useEffect(() => {
    const refresh = async () => {
      try {
        await refreshMediaRecorder()
      } catch (error) {
        datadogLogs.logger.error(
          '[Audio Capture] Error refreshing media recorder',
          {
            ...logDataRef.current,
            error
          }
        )
      }
    }

    if (selectedDevice && isRecording) {
      refresh()
    }
  }, [selectedDevice, isRecording, disableEchoCancelation])

  useEffect(() => {
    if (selectedDevice && isTesting) {
      const initStream = async () => {
        try {
          await createAudioStream()
        } catch (error) {
          datadogLogs.logger.error(
            '[Audio Capture] Error creating audio stream',
            {
              ...logDataRef.current,
              error
            }
          )
        }
      }
      initStream()
    }
  }, [selectedDevice, isTesting])

  useEffect(() => {
    if (audioStreamRef.current) {
      audioStreamRef.current
        .getAudioTracks()
        .forEach(t => (t.enabled = !isMuted))
    }
  }, [isMuted])

  // Mic warning after 15s (start)
  useEffect(() => {
    if (!isRecording || isMuted || !!audioLastDetectedAt) return

    const micWarning = setInterval(() => {
      if (!audioLastDetectedAt) {
        setActiveWarning('mic')
      }
    }, 15000)

    return () => {
      clearInterval(micWarning)
    }
  }, [isRecording, isMuted, audioLastDetectedAt])

  // Tab mic warning after 120s (start), then every 15s as long as there is no tab audio
  useEffect(() => {
    if (!isRecording || isMuted || !!tabAudioLastDetectedAt) {
      return
    }

    let warningTimeout: NodeJS.Timeout | null = null
    let warningInterval: NodeJS.Timeout | null = null

    warningTimeout = setTimeout(() => {
      if (!tabAudioLastDetectedAt && !!tabStreamRef.current) {
        setActiveWarning('tab')
        // After first warning, start interval for repeated warnings every 15s
        warningInterval = setInterval(() => {
          if (!tabAudioLastDetectedAt && !!tabStreamRef.current) {
            setActiveWarning('tab')
          }
        }, 15000)
      }
    }, 120000)

    return () => {
      if (warningTimeout) clearTimeout(warningTimeout)
      if (warningInterval) clearInterval(warningInterval)
    }
  }, [isRecording, isMuted, tabAudioLastDetectedAt])

  // Mic warning after 60s of no audio from either mic during session
  useEffect(() => {
    if (!isRecording || isMuted) return

    const interval = setInterval(() => {
      const now = Date.now()
      const noMicAudio =
        !audioLastDetectedAt || now - audioLastDetectedAt > 60000
      const noTabAudio =
        !tabAudioLastDetectedAt || now - tabAudioLastDetectedAt > 60000
      if (noMicAudio && noTabAudio) {
        setActiveWarning('mic')
      }
    }, 60000)

    return () => clearInterval(interval)
  }, [isRecording, isMuted, audioLastDetectedAt, tabAudioLastDetectedAt])

  const getInputDevices = async () => {
    datadogLogs.logger.info(
      '[Audio Capture] Getting input devices',
      logDataRef.current
    )
    const devices = await navigator.mediaDevices.enumerateDevices()
    const microphoneDevices = devices.filter(
      device => device.kind === 'audioinput' && !!device.deviceId
    )

    setDevices(microphoneDevices)
    return microphoneDevices
  }

  const handlePromptForDevicePermissions = async () => {
    try {
      datadogLogs.logger.info('[Audio Capture] Prompting for permissions')
      setPermissionStatus('requested')
      await createAudioStream()
      await getInputDevices()
      setPermissionStatus('granted')
      setIsTesting(true)
    } catch (err) {
      datadogLogs.logger.info(
        '[Audio Capture] Prompting for permissions error',
        { err, ...logDataRef.current }
      )
      setPermissionStatus('denied')
      setHasMicAccess(false)
    }
  }

  const testAudioInputs = useCallback(async () => {
    if (permissionStatus === 'granted') {
      setIsTesting(true)
    }
  }, [permissionStatus])

  const createAudioStream = async () => {
    // Force echo cancellation ON when sharing tab audio
    const shouldForceEchoCancellation = !!tabStreamRef.current

    let constraints: MediaStreamConstraints = {
      audio: {
        echoCancellation:
          shouldForceEchoCancellation || !disableEchoCancelation,
        autoGainControl: shouldForceEchoCancellation || !disableEchoCancelation
      }
    }

    if (selectedDevice) {
      constraints = {
        audio: {
          deviceId: { exact: selectedDevice.deviceId },
          echoCancellation:
            shouldForceEchoCancellation || !disableEchoCancelation,
          autoGainControl:
            shouldForceEchoCancellation || !disableEchoCancelation
        }
      }
    }

    datadogLogs.logger.info('[Audio Capture] Creating audio stream', {
      constraints,
      ...logDataRef.current
    })

    const stream = await navigator.mediaDevices.getUserMedia(constraints)
    streamsRef.current = [...streamsRef.current, stream]

    const audioContext = new (
      window.AudioContext ||
      // @ts-ignore
      window.webkitAudioContext
    )()
    audioContextRef.current = audioContext

    const micAnalyzer = audioContext.createAnalyser()
    const tabAnalyzer = audioContext.createAnalyser()
    const micSource = audioContext.createMediaStreamSource(stream)
    const dest = audioContext.createMediaStreamDestination()

    if (isClientChanneledAudioEnabled()) {
      dest.channelCount = 2
      dest.channelCountMode = 'explicit'

      datadogLogs.logger.info(
        '[Audio Capture] Creating stereo stream - Mic: Left, Tab: Right',
        {
          ...logDataRef.current,
          stereoMode: true,
          channels: {
            left: 'microphone',
            right: 'tab_audio'
          },
          outputChannels: 2,
          isClientChanneledAudioEnabled: true
        }
      )

      const merger = audioContext.createChannelMerger(2)

      const micGainNode = audioContext.createGain()
      const tabGainNode = audioContext.createGain()

      micSource.connect(micGainNode)
      micSource.connect(micAnalyzer)
      micGainNode.connect(merger, 0, 0) // output 0 to input 0 (left channel)

      const tabSource = audioContext.createMediaStreamSource(
        tabStreamRef.current!
      )

      tabSource.connect(tabGainNode)
      tabSource.connect(tabAnalyzer)
      tabGainNode.connect(merger, 0, 1) // output 0 to input 1 (right channel)

      merger.connect(dest)
    } else {
      datadogLogs.logger.info('[Audio Capture] Creating mono stream', {
        ...logDataRef.current,
        stereoMode: false,
        hasTabStream: !!tabStreamRef.current,
        isClientChanneledAudioEnabled: false
      })

      const gainNode = audioContext.createGain()

      micSource.connect(gainNode)
      micSource.connect(micAnalyzer)
      micSource.connect(dest)

      if (tabStreamRef.current) {
        datadogLogs.logger.info('[Audio Capture] Attempting to mix streams', {
          ...logDataRef.current
        })
        const tabSource = audioContext.createMediaStreamSource(
          tabStreamRef.current
        )
        tabSource.connect(gainNode)
        tabSource.connect(tabAnalyzer)
        tabSource.connect(dest)
      }
    }

    const micArray = new Uint8Array(micAnalyzer.fftSize)
    const tabArray = new Uint8Array(tabAnalyzer.fftSize)

    let micLastDetected = Date.now()
    let tabLastDetected = Date.now()

    function getPeakLevel(analyzer: AnalyserNode, array: Uint8Array) {
      analyzer.getByteTimeDomainData(array)
      return (
        array.reduce(
          (max, current) => Math.max(max, Math.abs(current - 127)),
          0
        ) / 128
      )
    }

    function tick() {
      if (isRecordingRef.current || isTestingRef.current) {
        const micPeak = getPeakLevel(micAnalyzer, micArray)
        const tabPeak = getPeakLevel(tabAnalyzer, tabArray)

        if (micPeak && micPeak > maxVolumeRef.current) {
          maxVolumeRef.current = micPeak
        }
        if (micPeak && micPeak > 0.01) {
          if (disableEchoCancelation) {
            volumeActions.setAudioLevel(Math.min(micPeak * 10, 0.99))
          } else {
            volumeActions.setAudioLevel(Math.min(micPeak * 2, 0.99))
          }
          setHasDetectedAudio(true)

          if (activeWarningRef.current === 'mic') {
            setActiveWarning(null)
          }

          if (Math.abs(Date.now() - micLastDetected) >= 5000) {
            if (isRecordingRef.current) {
              setAudioLastDetectedAt(Date.now())
            }
            micLastDetected = Date.now()
          }
        } else {
          volumeActions.setAudioLevel(0)
        }

        if (tabPeak && tabPeak > maxTabVolumeRef.current) {
          maxTabVolumeRef.current = tabPeak
        }
        if (tabPeak && tabPeak > 0.01) {
          volumeActions.setTabAudioLevel(Math.min(tabPeak, 0.99))
          setHasDetectedTabAudio(true)
          if (activeWarningRef.current === 'tab') {
            setActiveWarning(null)
          }
          if (Math.abs(Date.now() - tabLastDetected) >= 5000) {
            if (isRecordingRef.current) {
              setTabAudioLastDetectedAt(Date.now())
            }
            tabLastDetected = Date.now()
          }
        } else {
          volumeActions.setTabAudioLevel(0)
        }
      } else {
        // Stop the interval when not recording or testing
        if (tickIntervalRef.current) {
          clearInterval(tickIntervalRef.current)
          tickIntervalRef.current = null
        }
      }
    }

    // Clear any existing interval before starting a new one
    if (tickIntervalRef.current) {
      clearInterval(tickIntervalRef.current)
    }

    // Start the interval to run tick every 100ms
    tickIntervalRef.current = setInterval(tick, 100)

    audioStreamRef.current = dest.stream
    return dest.stream
  }

  const stopAudioStream = () => {
    streamsRef.current.forEach(s => s.getTracks().forEach(t => t.stop()))
    volumeActions.setAudioLevel(0)
  }

  const stopAudioShare = () => {
    if (tabStreamRef.current) {
      tabStreamRef.current.getTracks().forEach(t => t.stop())
    }
  }

  const stopRecording = async () => {
    // Clear the tick interval when stopping recording
    if (tickIntervalRef.current) {
      clearInterval(tickIntervalRef.current)
      tickIntervalRef.current = null
    }

    if (isRecordingRef.current) {
      datadogLogs.logger.info('[Audio Capture] Stop recording', {
        ...logDataRef.current
      })
      setIsRecording(false)
      isRecordingRef.current = false
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state === 'recording'
      ) {
        mediaRecorderRef.current.requestData()
        mediaRecorderRef.current?.stop()
        setIsLastChunkSet(true)
        isLastChunkSetRef.current = true
        stopAudioStream()
        stopAudioShare()
        setIsTesting(false)
      }
      if (uploadQueue.length > 0) {
        await new Promise(resolve => {
          // wait for the upload to complete so the UI can respond
          const interval = setInterval(() => {
            if (isUploadCompleteRef.current) {
              clearInterval(interval)
              resolve(true)
            }
          }, 100)
        })
      }
    } else {
      setIsTesting(false)
      stopAudioStream()
    }
  }

  useEffect(() => {
    if (
      !isRecording &&
      !isTesting &&
      isLastChunkSet &&
      uploadQueue.length === 0 &&
      !isUploadComplete &&
      !isUploading
    ) {
      datadogLogs.logger.info('[Audio Capture] Stuck Audio upload', {
        ...logDataRef.current
      })
    }
  }, [
    isRecording,
    uploadQueue,
    isUploadComplete,
    isTesting,
    isLastChunkSet,
    isUploading
  ])

  useEffect(() => {
    if (uploadQueue.length === 0 && isLastChunkSetRef.current) {
      isUploadCompleteRef.current = true
      setIsUploadComplete(true)
    }
  }, [uploadQueue])

  const getPresignedUrlV2 = async ({
    sessionId,
    timestamp
  }: {
    sessionId: string
    timestamp: number
  }) => {
    if (uploadUrl) {
      const response: any = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${uploadToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionId,
          timestamp,
          ...(isClientChanneledAudioEnabled() && {
            metadata: {
              'is-client-channeled-audio-enabled': true
            }
          })
        })
      })

      if (response.ok) {
        const data = await response.json()
        datadogLogs.logger.debug('[Audio Capture] Fetched v2 presigned URL', {
          ...logDataRef.current,
          presignedUrl: data.presignedUrl,
          payload: {
            timestamp,
            isClientChanneledAudioEnabled: isClientChanneledAudioEnabled()
          }
        })
        return data.presignedUrl
      } else {
        const errorData = await response.json()
        datadogLogs.logger.error(
          '[Audio Capture] Failed to fetch v2 presigned URL',
          {
            ...logDataRef.current,
            payload: {
              timestamp,
              isClientChanneledAudioEnabled: isClientChanneledAudioEnabled()
            },
            errorData: errorData
          }
        )
        throw new Error(errorData.message)
      }
    }
  }

  const uploadChunk = async (
    payload: UploadPayload,
    retryCount: number = 0
  ) => {
    const baseDelay = 1000

    setIsUploading(true)

    const { timestamp, chunks } = payload
    const blob = new Blob(chunks)

    try {
      if (chunks.length) {
        datadogLogs.logger.debug('[Audio Capture] Fetching presigned URL', {
          ...logDataRef.current,
          payload: {
            timestamp,
            isClientChanneledAudioEnabled: isClientChanneledAudioEnabled()
          }
        })

        let presignedUrl

        if (uploadUrl) {
          presignedUrl = await getPresignedUrlV2({
            sessionId: sessionId!,
            timestamp
          })
        } else {
          const data = await getPresignedUrl({
            sessionId,
            timestamp,
            ...(isClientChanneledAudioEnabled() && {
              metadata: {
                'is-client-channeled-audio-enabled': true
              }
            })
          })

          // @ts-ignore
          presignedUrl = data.url
        }

        datadogLogs.logger.debug('[Audio Capture] Uploading chunk', {
          ...logDataRef.current,
          presignedUrl,
          payload: {
            timestamp,
            isClientChanneledAudioEnabled: isClientChanneledAudioEnabled()
          }
        })

        await fetch(presignedUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': 'audio/ogg'
          },
          body: blob
        })
      }
      lastSuccessfulUploadAtRef.current = Date.now()
      retryCountRef.current = 0
      setUploadCount(prevCount => prevCount + 1)

      setUploadQueue(prevQueue => prevQueue.slice(1))

      setTimeout(() => {
        setIsUploading(false) // add a buffer so server doesn't get slammed if client goes offline and queue needs to drain
      }, 500)
    } catch (error) {
      const delay = baseDelay * Math.min(2 ** retryCount, 20)
      retryCountRef.current = retryCount + 1
      datadogLogs.logger.error(
        `[Audio Capture] Upload failed. Retry attempt ${retryCount + 1}. Retrying in ${delay} milliseconds.`,
        {
          ...logDataRef.current,
          uploadQueue: uploadQueueRef.current.length
        },
        error as Error
      )
      if (uploadTimeoutRef.current) {
        clearTimeout(uploadTimeoutRef.current)
      }
      uploadTimeoutRef.current = setTimeout(() => {
        uploadChunk(payload, retryCount + 1)
      }, delay)
    }
  }

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null
    if (isRecording && selectedDevice) {
      if (interval) {
        clearInterval(interval)
      }
      interval = setInterval(() => {
        if (
          mediaRecorderRef.current &&
          mediaRecorderRef.current.state === 'recording'
        ) {
          mediaRecorderRef.current.stop()
          if (isRecordingRef.current) {
            startMediaRecorder()
          }
        }
      }, 5000)
    } else {
      if (interval) {
        clearInterval(interval)
      }
    }

    return () => {
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [isRecording, selectedDevice])

  const createMediaRecorder = async (audioStream: MediaStream) => {
    datadogLogs.logger.info('[Audio Capture] Create media recorder', {
      ...logDataRef.current
    })

    const audioChunks: BlobPart[] = []
    const mediaRecorder = new MediaRecorder(audioStream)
    mediaRecordersRef.current.push(mediaRecorder)

    mediaRecorderRef.current = mediaRecorder

    mediaRecorder.addEventListener('dataavailable', async event => {
      if (event.data.size > 0) {
        audioChunks.push(event.data)
      }
    })

    mediaRecorder.addEventListener('stop', async () => {
      const chunks = audioChunks.splice(0, audioChunks.length)
      const payload = {
        timestamp: Date.now(),
        chunks
      }
      datadogLogs.logger.debug('[Audio Capture] Add chunk to queue', {
        ...logDataRef.current,
        activeMediaRecorders: mediaRecordersRef.current.filter(
          mr => mr.state === 'recording'
        ).length
      })

      setUploadQueue(prevQueue => [...prevQueue, payload])
    })

    return mediaRecorder
  }

  const refreshMediaRecorder = async () => {
    datadogLogs.logger.info('[Audio Capture] Refresh media recorder', {
      ...logDataRef.current
    })
    mediaRecordersRef.current.forEach(mr => {
      mr.stop()
      mr.ondataavailable = null
      mr.onstop = null
    })
    stopAudioStream()
    try {
      const stream = await createAudioStream()
      await createMediaRecorder(stream)
      startMediaRecorder()
    } catch (error) {
      datadogLogs.logger.error(
        '[Audio Capture] Error refreshing media recorder',
        {
          ...logDataRef.current,
          error: error
        }
      )
    }
  }

  const handleInputChange = async (deviceId: string) => {
    datadogLogs.logger.info('[Audio Capture] Changing inputs', {
      ...logDataRef.current,
      deviceId
    })
    const device = devices.find(d => d.deviceId === deviceId)
    if (device) {
      setSelectedDevice(device)
      setSelectedDeviceLabel(device.label)
      localStorage.setItem('microphoneDeviceId', deviceId)
      if (audioContextRef.current) {
        try {
          await audioContextRef.current.close()
        } catch (err) {
          datadogLogs.logger.warn(
            '[Audio Capture] Unable to close audio context',
            logDataRef.current,
            err as Error
          )
        }
      }
      setAudioLastDetectedAt(null)
    }
    maxVolumeRef.current = 0
    maxTabVolumeRef.current = 0
  }

  const startMediaRecorder = () => {
    if (mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state !== 'recording') {
        mediaRecorderRef.current.start()
      }
    }
  }

  const toggleMute = () => {
    setIsMuted(!isMuted)
  }

  const startRecording = async ({
    isUsingHeadphones,
    isTelehealth,
    telehealthAudioSource,
    isHighQualityAudio
  }: {
    isUsingHeadphones: boolean
    isTelehealth: boolean
    telehealthAudioSource: TelehealthAudioSource | null
    isHighQualityAudio: boolean | null
  }) => {
    if (isRecording) return

    if (
      isTelehealth &&
      isTelehealthSourceRequiredEnabled &&
      !telehealthAudioSource
    ) {
      datadogLogs.logger.warn(
        '[Audio Capture] Cannot start recording - telehealth source required',
        {
          ...logDataRef.current
        }
      )
      setTelehealthSourceError('Please select an option')
      return
    }

    datadogLogs.logger.info('[Audio Capture] Start recording', {
      ...logDataRef.current
    })

    if (!selectedDevice) {
      datadogLogs.logger.info(
        '[Audio Capture] Start recording - Prompting for permissions',
        { ...logDataRef.current }
      )
      // safari requires access on each page load
      await handlePromptForDevicePermissions()
    }

    try {
      await updateSession({
        sessionId,
        isTelehealth,
        isUsingHeadphones,
        telehealthAudioSource,
        isHighQualityAudio,
        useV2PresignedUrl: isPresignedUrlV2Enabled,
        isTelehealthSourceExperiment: isTelehealthSourceRequiredEnabled
      })
    } catch (error) {
      datadogLogs.logger.error('[Audio Capture] Failed to update session', {
        ...logDataRef.current,
        error
      })
      return
    }

    await handleMarkOnboardingComplete()

    try {
      const values = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || '{}')
      values[sessionId!] = localRecordingId
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(values))
    } catch (err) {
      datadogLogs.logger.info('[Audio Capture] Unable to check localStorage', {
        ...logDataRef.current,
        error: err
      })
    }

    isRecordingRef.current = true
    setIsRecording(true)
    setIsTesting(false)
    setActiveWarning(null)
  }

  /**
   * Starts content sharing in order to capture audio from the client's browser.
   *
   * 1. Gets the display media stream
   * 2. Checks if the content share is valid
   * 3. Sets the media stream to the tab stream
   * 4. Refreshes the media recorder
   *
   * Note: Firefox and Safari do not support content sharing.
   * @returns true if the content share was successful, false otherwise
   */
  const startContentShare = async (): Promise<
    'canceled' | 'unsupported' | 'success'
  > => {
    try {
      datadogLogs.logger.info(
        '[Audio Share] Attempting audio share',
        logDataRef.current
      )

      let mediaStream: MediaStream

      if (!isAudioScreenshareWarningEnabled) {
        mediaStream = await navigator.mediaDevices.getDisplayMedia({
          audio: true,
          video: true,
          // @ts-ignore
          selfBrowserSurface: isExtension ? ['include'] : ['exclude']
        })

        // Validate audio stream if feature flag is enabled
        if (isValidateAudioStreamEnabled) {
          const audioTracks = mediaStream.getAudioTracks()
          if (audioTracks.length === 0) {
            datadogLogs.logger.warn(
              '[Audio Share] No audio tracks found in stream',
              logDataRef.current
            )
            mediaStream.getTracks().forEach(t => t.stop())
            setActiveWarning('audio-stream')
            playAlertSound()
            return 'unsupported'
          }

          // Check if audio track is enabled and not muted
          const audioTrack = audioTracks[0]
          if (!audioTrack.enabled || audioTrack.muted) {
            datadogLogs.logger.warn(
              '[Audio Share] Audio track is disabled or muted',
              {
                ...logDataRef.current,
                enabled: audioTrack.enabled,
                muted: audioTrack.muted
              }
            )
            mediaStream.getTracks().forEach(t => t.stop())
            setActiveWarning('audio-stream')
            playAlertSound()
            return 'unsupported'
          }

          // Attempt to create an audio context to verify the track works
          try {
            const testContext = new AudioContext()
            const testSource = testContext.createMediaStreamSource(mediaStream)
            const testAnalyzer = testContext.createAnalyser()
            testSource.connect(testAnalyzer)

            // Clean up test context
            testContext.close()

            datadogLogs.logger.info(
              '[Audio Share] Audio stream validation successful',
              logDataRef.current
            )
          } catch (audioError) {
            datadogLogs.logger.error(
              '[Audio Share] Failed to create audio context from stream',
              {
                ...logDataRef.current,
                error: audioError
              }
            )
            mediaStream.getTracks().forEach(t => t.stop())
            setActiveWarning('audio-stream')
            playAlertSound()
            return 'unsupported'
          }
        }

        return 'success'
      } else {
        const base = {
          audio: true,
          video: true
        }

        // Chromium browsers support selfBrowserSurface, which allows us to disable sharing the entire screen for Chrome, Edge, and Opera
        // Content sharing is not currently supported for Firefox and Safari on Blueprint's platform.
        const constraints = isChromiumBrowser()
          ? {
              ...base,
              selfBrowserSurface: isExtension ? ['include'] : ['exclude'],
              ...(!isWindows() && { monitorTypeSurfaces: ['exclude'] })
            }
          : base

        mediaStream = await navigator.mediaDevices.getDisplayMedia(constraints)

        const [videoTrack] = mediaStream.getVideoTracks()
        const videoSettings = videoTrack.getSettings()

        const { displaySurface } = videoSettings as {
          displaySurface?: string
        }

        const displayType =
          displaySurface === undefined
            ? 'screen or window'
            : displaySurface === 'monitor'
              ? 'screen'
              : 'window'
        setContentShareDisplayType(displayType)

        // Check if audio sharing is not supported for the display surface
        const isUnsupportedSharing = isWindows()
          ? isWholeWindow(videoSettings) // Windows: window sharing is not supported
          : isWholeScreen(videoSettings) || isWholeWindow(videoSettings) // Non-Windows: both screen and window are not supported

        if (isUnsupportedSharing) {
          // Immediately stop track streaming
          mediaStream.getTracks().forEach(t => t.stop())
          setActiveWarning('screenshare')
          playAlertSound()
          return 'unsupported'
        }

        // Validate audio stream if feature flag is enabled
        if (isValidateAudioStreamEnabled) {
          const audioTracks = mediaStream.getAudioTracks()
          if (audioTracks.length === 0) {
            datadogLogs.logger.warn(
              '[Audio Share] No audio tracks found in stream',
              {
                ...logDataRef.current,
                displayType
              }
            )
            mediaStream.getTracks().forEach(t => t.stop())
            setActiveWarning('audio-stream')
            playAlertSound()
            return 'unsupported'
          }

          // Check if audio track is enabled and not muted
          const audioTrack = audioTracks[0]
          if (!audioTrack.enabled || audioTrack.muted) {
            datadogLogs.logger.warn(
              '[Audio Share] Audio track is disabled or muted',
              {
                ...logDataRef.current,
                displayType,
                enabled: audioTrack.enabled,
                muted: audioTrack.muted
              }
            )
            mediaStream.getTracks().forEach(t => t.stop())
            setActiveWarning('audio-stream')
            playAlertSound()
            return 'unsupported'
          }

          // Attempt to create an audio context to verify the track works
          try {
            const testContext = new AudioContext()
            const testSource = testContext.createMediaStreamSource(mediaStream)
            const testAnalyzer = testContext.createAnalyser()
            testSource.connect(testAnalyzer)

            testContext.close()

            datadogLogs.logger.info(
              '[Audio Share] Audio stream validation successful',
              {
                ...logDataRef.current,
                displayType
              }
            )
          } catch (audioError) {
            datadogLogs.logger.error(
              '[Audio Share] Failed to create audio context from stream',
              {
                ...logDataRef.current,
                displayType,
                error: audioError
              }
            )
            mediaStream.getTracks().forEach(t => t.stop())
            setActiveWarning('audio-stream')
            playAlertSound()
            return 'unsupported'
          }
        }
      }

      // --- Handle stream tracks ---
      mediaStream.getTracks().forEach((track: MediaStreamTrack) => {
        track.onended = () => {
          tabStreamRef.current = null
          refreshMediaRecorder()
        }
      })

      tabStreamRef.current = mediaStream // do not set on state to make sure we don't end it

      if (isRecordingRef.current) {
        await refreshMediaRecorder()
      }

      return 'success'
    } catch (error) {
      // Special case: User cancelled the screen sharing dialog, so skip logging in those cases
      if (
        !(error instanceof DOMException && error.name === 'NotAllowedError')
      ) {
        datadogLogs.logger.warn(
          '[Audio Share] Error sharing audio',
          {
            ...logDataRef.current
          },
          error as Error
        )
      }
      return 'canceled'
    }
  }

  const handleSetIsStopRecordingAfterEnabled = (enabled: boolean) => {
    if (enabled) {
      datadogLogs.logger.info(
        '[Audio Capture] Enable auto-stop recording',
        logDataRef.current
      )
    } else {
      datadogLogs.logger.info(
        '[Audio Capture] Disable auto-stop recording',
        logDataRef.current
      )
    }
    setIsStopRecordingAfterEnabled(enabled)
  }

  // Cleanup effect to clear tick interval on unmount
  useEffect(() => {
    return () => {
      if (tickIntervalRef.current) {
        clearInterval(tickIntervalRef.current)
        tickIntervalRef.current = null
      }
    }
  }, [])

  const contextValue = {
    isReady: true,
    audioInputs: devices,
    selectedAudioInput: selectedDevice?.deviceId || '',
    setSelectedAudioInput: handleInputChange,
    testAudioInputs,
    hasMicAccess,
    setHasMicAccess,
    promptForDevicePermissions: handlePromptForDevicePermissions,
    isMuted,
    toggleMute,
    disableEchoCancelation: setDisableEchoCancelation,
    isRecording,
    isRecordingLoading: isUpdateSessionLoading,
    startRecording,
    stopRecording,
    startContentShare,
    stopContentShare: () => {},
    isTabAudioStreamOpen: !!tabStreamRef.current,
    logData: logDataRef.current,
    hasDetectedAudio,
    hasDetectedTabAudio,
    showMicWarning,
    showTabMicWarning,
    showScreenShareWarning,
    showAudioStreamWarning,
    activeWarning: activeWarningRef.current,
    setActiveWarning,
    isUsingHeadphones,
    setIsUsingHeadphones,
    isTelehealth,
    setIsTelehealth,
    telehealthAudioSource,
    setTelehealthAudioSource,
    telehealthSourceError,
    qualityToggle,
    setQualityToggle,
    isUploadComplete,
    selectedAudioInputLabel,
    isSettingsOpen,
    setIsSettingsOpen,
    stopRecordingAfter,
    setStopRecordingAfter,
    isStopRecordingAfterEnabled,
    setIsStopRecordingAfterEnabled: handleSetIsStopRecordingAfterEnabled,
    hasResolvedInputDevicePermissions,
    permissionStatus,
    uploadQueueSize: uploadQueue.length,
    contentShareDisplayType,
    setContentShareDisplayType,
    isTelehealthSourceRequiredEnabled
  }

  return (
    <AudioCaptureContext.Provider value={contextValue}>
      {children}
    </AudioCaptureContext.Provider>
  )
}

export default ManualAudioCaptureProviderV2
